// components/ForgotPasswordPage.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Alert,
    Spinner,
    CardText
} from 'reactstrap';
import { supabase } from '../../lib/supabase';

const ForgotPasswordPage = () => {
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ visible: false, message: '', type: '' });
    const [email, setEmail] = useState('');
    const [emailSent, setEmailSent] = useState(false);
    const [countdown, setCountdown] = useState(0);

    const showAlert = (message, type = 'info') => {
        setAlert({ visible: true, message, type });
        setTimeout(() => setAlert({ visible: false, message: '', type: '' }), 5000);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!email.trim()) {
            showAlert('Please enter your email address', 'danger');
            setLoading(false);
            return;
        }

        try {
            // Supabase reset password email
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/reset-password`,
            });

            if (error) {
                // Don't reveal if email exists or not for security
                console.log('Reset email error:', error);
            }

            // Always show success message for security (prevents email enumeration)
            showAlert('If an account exists with this email, you will receive a password reset link shortly.', 'success');
            setEmailSent(true);

            // Start countdown for resend (60 seconds)
            setCountdown(60);
            const interval = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

        } catch (error) {
            // Don't show specific error for security
            console.error('Forgot password error:', error);
            showAlert('If an account exists with this email, you will receive a password reset link shortly.', 'info');
            setEmailSent(true);
        } finally {
            setLoading(false);
        }
    };

    const handleResendEmail = async () => {
        if (countdown > 0 || !email.trim()) return;

        setLoading(true);
        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/reset-password`,
            });

            if (error) {
                console.log('Resend email error:', error);
            }

            showAlert('If needed, another reset link has been sent.', 'info');
            setCountdown(60);

            const interval = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

        } catch (error) {
            console.error('Resend email error:', error);
            showAlert('If needed, another reset link has been sent.', 'info');
        } finally {
            setLoading(false);
        }
    };

    // SVG Pattern as base64
    const svgPattern = `
    <svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400">
      <defs>
        <linearGradient id="darkBlueGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#0a192f"/>
          <stop offset="100%" stop-color="#112240"/>
        </linearGradient>
        <linearGradient id="orangeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#ff7e5f"/>
          <stop offset="100%" stop-color="#feb47b"/>
        </linearGradient>
        <linearGradient id="silverGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#e6e8ec"/>
          <stop offset="100%" stop-color="#f5f7fa"/>
        </linearGradient>
        <pattern id="authPattern" patternUnits="userSpaceOnUse" width="200" height="200" patternTransform="rotate(45)">
          <rect width="200" height="200" fill="url(%23darkBlueGrad)" opacity="0.95"/>
          <circle cx="50" cy="50" r="30" fill="url(%23orangeGrad)" opacity="0.7"/>
          <circle cx="150" cy="150" r="20" fill="url(%23orangeGrad)" opacity="0.5"/>
          <rect x="120" y="20" width="60" height="60" fill="url(%23silverGrad)" opacity="0.3" transform="rotate(45 150 50)"/>
          <rect x="20" y="120" width="60" height="60" fill="url(%23silverGrad)" opacity="0.3" transform="rotate(45 50 150)"/>
          <line x1="0" y1="100" x2="200" y2="100" stroke="url(%23silverGrad)" stroke-width="1" opacity="0.2"/>
          <line x1="100" y1="0" x2="100" y2="200" stroke="url(%23silverGrad)" stroke-width="1" opacity="0.2"/>
          <line x1="0" y1="0" x2="200" y2="200" stroke="url(%23orangeGrad)" stroke-width="2" opacity="0.1"/>
          <line x1="200" y1="0" x2="0" y2="200" stroke="url(%23orangeGrad)" stroke-width="2" opacity="0.1"/>
          <circle cx="25" cy="25" r="2" fill="#f5f7fa" opacity="0.4"/>
          <circle cx="75" cy="75" r="2" fill="#f5f7fa" opacity="0.4"/>
          <circle cx="125" cy="125" r="2" fill="#f5f7fa" opacity="0.4"/>
          <circle cx="175" cy="175" r="2" fill="#f5f7fa" opacity="0.4"/>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(%23authPattern)"/>
      <rect width="100%" height="100%" fill="url(%23darkBlueGrad)" opacity="0.1"/>
    </svg>
    `;

    const svgDataUrl = `data:image/svg+xml;base64,${btoa(svgPattern)}`;

    return (
        <div className="richads-forgot-password" style={styles.pageContainer}>
            {/* Alert */}
            {alert.visible && (
                <Alert color={alert.type} className="fixed-top m-3" style={{ zIndex: 1050 }}>
                    {alert.message}
                </Alert>
            )}

            {/* SVG Pattern Background */}
            <div
                className="auth-pattern-background"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: `url("${svgDataUrl}")`,
                    backgroundSize: '400px 400px',
                    backgroundPosition: 'center',
                    opacity: 0.9,
                    zIndex: 0
                }}
            />

            {/* Gradient Overlay */}
            <div
                className="gradient-overlay"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(10, 25, 47, 0.9) 0%, rgba(17, 34, 64, 0.8) 100%)',
                    zIndex: 1
                }}
            />

            <Container className="auth-container py-5" style={{ position: 'relative', zIndex: 2 }}>
                <Row className="justify-content-center align-items-center min-vh-100">
                    <Col lg={5} md={7} sm={10}>
                        {!emailSent ? (
                            <Card className="richads-card" style={styles.card}>
                                <CardBody className="p-4">
                                    {/* Logo and Title */}
                                    <div className="text-center mb-4">
                                        <div className="mb-3">
                                            <div className="password-icon" style={styles.passwordIcon}>
                                                <i className="fas fa-key" style={styles.icon}></i>
                                            </div>
                                        </div>
                                        <CardTitle tag="h2" className="fw-bold mb-2" style={styles.title}>
                                            Forgot Password?
                                        </CardTitle>
                                        <CardText style={styles.subtitle}>
                                            Enter your email address and we'll send you a link to reset your password.
                                        </CardText>
                                    </div>

                                    <Form onSubmit={handleSubmit}>
                                        {/* Email Input */}
                                        <FormGroup className="mb-4">
                                            <Label for="email" className="fw-bold" style={styles.label}>
                                                <i className="fas fa-envelope me-2" style={{ color: '#ff7e5f' }}></i>
                                                Email Address
                                            </Label>
                                            <Input
                                                type="email"
                                                name="email"
                                                id="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                                placeholder="Enter your registered email"
                                                style={styles.input}
                                                className="input-focus"
                                                autoComplete="email"
                                            />
                                        </FormGroup>

                                        {/* Submit Button */}
                                        <Button
                                            color="primary"
                                            type="submit"
                                            disabled={loading}
                                            className="w-100 btn-primary py-2"
                                            size="lg"
                                            style={styles.button}
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" />
                                                    Sending Link...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-paper-plane me-2"></i>
                                                    Send Reset Link
                                                </>
                                            )}
                                        </Button>
                                    </Form>

                                    {/* Back to Login */}
                                    <div className="text-center mt-4">
                                        <Link
                                            to="/login"
                                            style={styles.backLink}
                                        >
                                            <i className="fas fa-arrow-left me-2"></i>
                                            Back to Sign In
                                        </Link>
                                    </div>
                                </CardBody>
                            </Card>
                        ) : (
                            <Card className="richads-card" style={styles.card}>
                                <CardBody className="p-4 text-center">
                                    {/* Success Icon */}
                                    <div className="mb-4">
                                        <div className="success-icon" style={styles.successIcon}>
                                            <i className="fas fa-check" style={{ fontSize: '2rem' }}></i>
                                        </div>
                                    </div>

                                    {/* Success Message */}
                                    <CardTitle tag="h3" className="fw-bold mb-3" style={styles.successTitle}>
                                        Check Your Email
                                    </CardTitle>

                                    <CardText style={styles.successText}>
                                        If an account exists with <strong style={{ color: '#feb47b' }}>{email}</strong>,
                                        you will receive a password reset link shortly.
                                    </CardText>

                                    <CardText style={styles.successText}>
                                        <i className="fas fa-clock me-2" style={{ color: '#ff7e5f' }}></i>
                                        The link will expire in 1 hour for security.
                                    </CardText>

                                    {/* What to do next */}
                                    <div className="mt-4 p-3" style={styles.infoBox}>
                                        <h6 style={{ color: '#feb47b', marginBottom: '1rem' }}>
                                            <i className="fas fa-info-circle me-2"></i>
                                            What to do next:
                                        </h6>
                                        <ul style={{
                                            textAlign: 'left',
                                            color: '#e6e8ec',
                                            fontSize: '0.85rem',
                                            paddingLeft: '1.5rem',
                                            marginBottom: 0
                                        }}>
                                            <li>Check your inbox for an email from Reachtix</li>
                                            <li>Click the password reset link in the email</li>
                                            <li>Create a new password for your account</li>
                                            <li>Sign in with your new password</li>
                                        </ul>
                                    </div>

                                    {/* Resend Email */}
                                    <div className="mt-4 mb-3">
                                        <Button
                                            color="outline-primary"
                                            onClick={handleResendEmail}
                                            disabled={countdown > 0 || loading}
                                            style={styles.resendButton}
                                        >
                                            {loading ? (
                                                <Spinner size="sm" className="me-2" />
                                            ) : (
                                                <i className="fas fa-redo me-2"></i>
                                            )}
                                            {countdown > 0 ? `Resend in ${countdown}s` : 'Resend Email'}
                                        </Button>
                                    </div>

                                    {/* Didn't Receive Email? */}
                                    <CardText style={styles.helpText}>
                                        <small>
                                            <i className="fas fa-exclamation-triangle me-2" style={{ color: '#ff7e5f' }}></i>
                                            Didn't receive the email? Check your spam folder or try again.
                                        </small>
                                    </CardText>

                                    {/* Back to Login */}
                                    <div className="mt-4 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                        <Link
                                            to="/login"
                                            style={styles.backLink}
                                        >
                                            <i className="fas fa-sign-in-alt me-2"></i>
                                            Return to Sign In
                                        </Link>
                                    </div>
                                </CardBody>
                            </Card>
                        )}

                        {/* Additional Help */}
                        <div className="text-center mt-4">
                            <small style={{ color: '#e6e8ec', fontSize: '0.85rem' }}>
                                <i className="fas fa-life-ring me-2" style={{ color: '#feb47b' }}></i>
                                Need help?{' '}
                                <Link
                                    to="/contact"
                                    style={{
                                        color: '#feb47b',
                                        textDecoration: 'none',
                                        fontWeight: '500'
                                    }}
                                >
                                    Contact Support
                                </Link>
                            </small>
                        </div>
                    </Col>
                </Row>
            </Container>

            {/* Add CSS styles */}
            <style jsx>{`
                @keyframes successPulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                    100% { transform: scale(1); }
                }
                
                .success-icon {
                    animation: successPulse 1.5s ease infinite;
                }
                
                .richads-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
                }
                
                .input-focus:focus {
                    background-color: rgba(230, 232, 236, 0.15) !important;
                    border-color: #ff7e5f !important;
                    box-shadow: 0 0 0 0.2rem rgba(255, 126, 95, 0.25) !important;
                    color: #f5f7fa !important;
                }
                
                .btn-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 25px rgba(255, 126, 95, 0.4) !important;
                }
                
                .btn-outline-primary:hover {
                    background-color: rgba(255, 126, 95, 0.1) !important;
                    transform: translateY(-2px);
                }
                
                .btn-primary:disabled,
                .btn-outline-primary:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                
                a:hover {
                    opacity: 0.8;
                }
            `}</style>
        </div>
    );
};

// Inline Styles
const styles = {
    pageContainer: {
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden'
    },
    card: {
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease'
    },
    passwordIcon: {
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, rgba(255, 126, 95, 0.2) 0%, rgba(254, 180, 123, 0.1) 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
        border: '2px solid rgba(255, 126, 95, 0.3)'
    },
    icon: {
        fontSize: '2.5rem',
        background: 'linear-gradient(90deg, #ff7e5f 0%, #feb47b 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
    },
    title: {
        background: 'linear-gradient(90deg, #ff7e5f 0%, #feb47b 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        fontSize: '1.8rem'
    },
    subtitle: {
        color: '#e6e8ec',
        fontSize: '0.95rem',
        marginTop: '0.5rem',
        lineHeight: '1.5'
    },
    label: {
        color: '#f5f7fa',
        marginBottom: '0.5rem',
        fontSize: '0.9rem',
        letterSpacing: '0.5px'
    },
    input: {
        backgroundColor: 'rgba(230, 232, 236, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        color: '#f5f7fa',
        borderRadius: '8px',
        padding: '12px 16px',
        fontSize: '0.95rem',
        transition: 'all 0.3s ease'
    },
    button: {
        background: 'linear-gradient(90deg, #ff7e5f 0%, #feb47b 100%)',
        border: 'none',
        borderRadius: '8px',
        fontWeight: '600',
        letterSpacing: '0.5px',
        transition: 'all 0.3s ease',
        padding: '14px',
        fontSize: '1rem'
    },
    backLink: {
        color: '#feb47b',
        fontWeight: '500',
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        transition: 'all 0.3s ease'
    },
    successIcon: {
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(102, 187, 106, 0.1) 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
        border: '2px solid rgba(76, 175, 80, 0.3)',
        color: '#4CAF50'
    },
    successTitle: {
        color: '#4CAF50',
        fontSize: '1.8rem'
    },
    successText: {
        color: '#e6e8ec',
        fontSize: '0.95rem',
        marginBottom: '1rem',
        lineHeight: '1.5'
    },
    infoBox: {
        background: 'rgba(10, 25, 47, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        borderLeft: '3px solid #ff7e5f'
    },
    resendButton: {
        borderColor: '#ff7e5f',
        color: '#ff7e5f',
        borderRadius: '8px',
        padding: '10px 24px',
        fontWeight: '500',
        transition: 'all 0.3s ease'
    },
    helpText: {
        color: '#e6e8ec',
        fontSize: '0.85rem',
        lineHeight: '1.4'
    }
};

export default ForgotPasswordPage;